import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { api } from '../services/firebaseFunctions';
import PageHeader from '../interaction/PageHeader';

function RoleManager() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const rolesData = await api.listRoles();
      setRoles(rolesData);
    } catch (err) {
      setError(err.message);
      console.error('Error loading roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        id: role.id,
        name: role.name,
        description: role.description || ''
      });
    } else {
      setEditingRole(null);
      setFormData({
        id: '',
        name: '',
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setFormData({
      id: '',
      name: '',
      description: ''
    });
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      
      if (editingRole) {
        await api.updateRole(editingRole.id, {
          name: formData.name,
          description: formData.description
        });
      } else {
        await api.createRole(formData);
      }
      
      handleCloseDialog();
      loadData();
    } catch (err) {
      setError(err.message);
      console.error('Error saving role:', err);
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Roles"
        subtitle="Roles follow a {service}_{level} name and are what backend services read from a user's token."
        className="ph--page"
        slot={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Create Role
          </Button>
        }
      />
      <Card>
        <CardContent>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by ID, name, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...roles]
                  .filter(role => {
                    const query = searchQuery.toLowerCase();
                    return !query || role.id?.toLowerCase().includes(query) || role.name?.toLowerCase().includes(query) || role.description?.toLowerCase().includes(query);
                  })
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {role.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{role.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {role.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(role)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Role' : 'Create New Role'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {!editingRole && (
              <TextField
                fullWidth
                label="Role ID"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                margin="normal"
                required
                helperText="Unique identifier for the role (lowercase, no spaces)"
              />
            )}
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              disabled={!!editingRole}
              helperText={editingRole ? "Role name cannot be changed after creation" : ""}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Optional description of the role"
            />

            {/* Role Naming Convention */}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Role Naming: service_level</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                <strong>Services:</strong> experience, sp3c, video, agent, flow
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Level suffixes:</strong><br/>
                • _admin (full service access)<br/>
                • _manager (manage resources)<br/>
                • _editor (create/edit content)<br/>
                • _viewer (read-only access)
              </Typography>
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                <strong>Global roles:</strong> system_admin, sofiapulse_admin, guest
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.name || (!editingRole && !formData.id)}
          >
            {editingRole ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default RoleManager;